// src/pages/MyGrowthPage/components/StatsGrid.jsx
import React from "react";
import styled from "styled-components";

const StatsGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 40px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.5s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    border-color: ${(props) => props.accentColor || "rgba(255, 255, 255, 0.1)"};
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) => props.accentColor || "var(--accentBlue)"};
  }
`;

const StatIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 15px;
  background: ${(props) => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 28px;
  color: ${(props) => props.color};
`;

const StatValue = styled.div`
  font-size: 2.8rem;
  font-weight: 500;
  margin-bottom: 5px;
  color: var(--primaryColor);
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: var(--fontSecondaryColor);
  margin-bottom: 10px;
  font-weight: 500;
`;

const StatSubtext = styled.div`
  font-size: 0.85rem;
  color: var(--fontSecondaryColor);
  opacity: 0.8;
`;

const ProgressBar = styled.div`
  height: 6px;
  background: var(--thirdBackgroundColor);
  border-radius: 3px;
  margin-top: 15px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => props.percent}%;
  background: ${(props) => props.color};
  border-radius: 3px;
  transition: width 1s ease-in-out;
`;

const TrendBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 8px;
  background: ${(props) =>
    props.positive ? "rgba(76, 175, 80, 0.2)" : "rgba(244, 67, 54, 0.2)"};
  color: ${(props) => (props.positive ? "#4caf50" : "#f44336")};
`;

const StatsGrid = ({
  growthScore,
  productivityVelocity,
  perfectDayStreak,
  currentLevel,
}) => {
  return (
    <StatsGridContainer>
      <StatCard accentColor="#4a90e2">
        <StatIcon color="#4a90e2">📈</StatIcon>
        <StatValue>
          {growthScore}
          <span style={{ fontSize: "1.5rem" }}>%</span>
        </StatValue>
        <StatLabel>Growth Score</StatLabel>
        <ProgressBar>
          <ProgressFill percent={growthScore} color="#4a90e2" />
        </ProgressBar>
        <StatSubtext>
          Last 7 days average
          {growthScore >= 90 && <TrendBadge positive>Excellent!</TrendBadge>}
        </StatSubtext>
      </StatCard>

      <StatCard accentColor="#4caf50">
        <StatIcon color="#4caf50">🚀</StatIcon>
        <StatValue>
          {productivityVelocity > 0 ? "+" : ""}
          {productivityVelocity}%
        </StatValue>
        <StatLabel>Productivity Velocity</StatLabel>
        <StatSubtext>
          vs previous period
          <TrendBadge positive={productivityVelocity > 0}>
            {productivityVelocity > 0
              ? "↑ Improving"
              : productivityVelocity < 0
              ? "↓ Needs work"
              : "→ Steady"}
          </TrendBadge>
        </StatSubtext>
      </StatCard>

      <StatCard accentColor="#ff9800">
        <StatIcon color="#ff9800">🔥</StatIcon>
        <StatValue>{perfectDayStreak}</StatValue>
        <StatLabel>Perfect Day Streak</StatLabel>
        <StatSubtext>
          Consecutive days with 100% completion
          {perfectDayStreak >= 3 && (
            <TrendBadge positive>On fire! 🔥</TrendBadge>
          )}
        </StatSubtext>
      </StatCard>

      <StatCard accentColor="#9c27b0">
        <StatIcon color="#9c27b0">⭐</StatIcon>
        <StatValue>{currentLevel.level}</StatValue>
        <StatLabel>{currentLevel.title}</StatLabel>
        <ProgressBar>
          <ProgressFill percent={currentLevel.progress} color="#9c27b0" />
        </ProgressBar>
        <StatSubtext>
          {currentLevel.xpInCurrentLevel} / {currentLevel.xpForNextLevel} XP to
          next level
        </StatSubtext>
      </StatCard>
    </StatsGridContainer>
  );
};

export default StatsGrid;
